<template>
  <div class="content-wrapper">
    <Loading v-if="isLoading" text="Loading List..." />

    <div v-else>
      <h2
        class="page-titles"
        contenteditable
        @input="handleEdit"
        @keydown.enter.prevent="(e) => e.target.blur(handleEdit)"
      >
        {{ listName.split('.csv')[0] }}
      </h2>

      <!-- Back arrow here -->
      <div class="arrow-link">
        <router-link to="/recipient-list">
          <span>Back to recipient lists</span>
        </router-link>
      </div>
      <div v-if="!loadStatus">
        <Skeleton width="100%" height="3.5rem" v-for="i in 15" :key="i" />
      </div>
      <!-- Table Here -->
      <div class="table-holder" v-else>
        <table>
          <!-- Table Head Here -->
          <thead>
            <tr>
              <td v-for="(header, index) in headers" :key="index">
                {{ header }}
              </td>
            </tr>
          </thead>

          <!-- Body Here -->
          <tbody>
            <tr v-for="recipient in recipients" :key="recipient.id">
              <td v-for="(header, index) in headers" :key="index">
                {{ handleField(recipient, header) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <Pagination
      :total-items="totalRecords"
      :page="serverParams.page"
      :items-per-page="serverParams.perPage"
      @page-change="pageChange"
      @perPageChange="handlePerPageChange"
    />
  </div>
</template>

<script>
import { layouts } from '../../helpers'
import { mapState } from 'vuex'
import { progressStatus } from '../../helpers'
import { debounce } from 'lodash'

export default {
  name: 'RecipientDetail',

  computed: {
    ...mapState({
      loading: (state) => state.recipient.loadRecipientList,
      recipientList: (state) => state.recipient.recipientListDetails,
      totalRecords: (state) => state.recipient.recipientTotal,
    }),
    recipients() {
      const recipients = this.recipientList || []
      return recipients.map((recipient) => {
        return {
          id: recipient.id,
          name: recipient.attributes && recipient.attributes.name,
          email: recipient.attributes && recipient.attributes.email,
          phone: recipient.attributes && recipient.attributes.phone,
          company: recipient.attributes && recipient.attributes.company,
          addressOne: recipient.attributes && recipient.attributes.addressOne,
          addressTwo: recipient.attributes && recipient.attributes.addressTwo,
          city: recipient.attributes && recipient.attributes.city,
          state: recipient.attributes && recipient.attributes.state,
          zipcode: recipient.attributes && recipient.attributes.zipcode,
          country: recipient.attributes && recipient.attributes.country,
        }
      })
    },

    loadStatus() {
      return progressStatus(this.loading)
    },
    listName() {
      return this.recipientList &&
        Array.isArray(this.recipientList) &&
        this.recipientList[0] &&
        this.recipientList[0].attributes
        ? this.recipientList[0].attributes.listName
        : ''
    },
  },

  data: () => ({
    isLoading: true,
    serverParams: {
      page: 0,
      perPage: 50,
    },
    headers: [
      'Name',
      'Email',
      'Phone',
      'Company',
      'Address  1',
      'Address  2',
      'City',
      'State',
      'Postal Code',
      'Country',
    ],
  }),
  components: {
    Pagination: () => import('../Universal/Pagination.vue'),
    Skeleton: () => import('@/components/Universal/Skeleton'),
  },
  created() {
    this.$store.dispatch('layout/setPageLayout', layouts.PAIGE_DASHBOARD)
    this.fetchRecipientListWithDelay()
  },

  methods: {
    handleEdit: debounce(function (e) {
      this.$store.dispatch('recipient/handleEditRecipientList', {
        id: this.$route.params.id,
        name: e.target.innerText,
      })
    }, 1500),

    pageChange(page) {
      this.updateParams({ page: page })
      this.getNextChuckOfList()
    },
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps)
    },
    handlePerPageChange(e) {
      this.serverParams.perPage = e
      this.getNextChuckOfList()
    },
    getNextChuckOfList() {
      this.$store.dispatch('recipient/getRecipientListDetail', {
        id: this.$route.params.id,
        page: 1,
        perPage: this.serverParams.perPage,
      })
    },

    handleField(recipient, header) {
      switch (header) {
        case 'Address  1':
          return recipient['addressOne']

        case 'Address  2':
          return recipient['addressTwo']

        case 'Postal Code':
          return recipient['zipcode']

        default:
          return recipient[header.toLowerCase()]
      }
    },
    async fetchRecipientListWithDelay() {
      this.isLoading = true

      await new Promise((resolve) => setTimeout(resolve, 5000))

      try {
        await this.$store.dispatch('recipient/getRecipientListDetail', {
          id: this.$route.params.id,
          page: 1,
          perPage: this.serverParams.perPage,
        })
      } catch (error) {
        console.error('Error fetching recipient list:', error)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.arrow-link {
  margin-top: 4rem;
}

.table-holder {
  margin-top: 2.5rem;
  overflow-x: scroll;
  overflow-y: hidden;
  height: 100%;

  &::-webkit-scrollbar {
    width: 10px;
    height: 4px;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: #979797;

    &:hover {
      background: #555;
    }
  }

  table {
    width: 100%;
    text-indent: 0;
    min-width: 100%;
    border-collapse: collapse;

    thead > tr > td {
      padding: 0.2rem 1rem;
      font-size: 0.875rem;
      font-weight: bold;
      text-align: left;
      padding-bottom: 1rem;
    }

    tbody > tr > td {
      width: 13rem;
      min-width: 9.5rem;
      max-width: 13rem;
      word-wrap: break-word;
      text-align: left;
      padding: 1.25rem 1rem;
      font-size: 0.813rem;
      border-bottom: 1px solid #979797;
    }
  }
}

[contenteditable]:focus {
  outline: 1px solid #9b9b9b;
}
</style>
